import { useState } from 'react'
import { Upload, FileText, Download, CheckCircle, AlertCircle, Zap, Calculator } from 'lucide-react'

export default function TaxPrepPage() {
  const [documents, setDocuments] = useState([
    { id: 1, name: 'W-2 Forms', category: 'Income', uploadDate: '2024-02-15', status: 'Uploaded' },
    { id: 2, name: '1099-NEC Forms', category: 'Income', uploadDate: '2024-02-10', status: 'Uploaded' },
    { id: 3, name: 'Charitable Donations', category: 'Deductions', uploadDate: '2024-01-20', status: 'Pending' },
  ])

  const [reportData, setReportData] = useState({
    totalIncome: 125000,
    deductions: 18500,
    taxableIncome: 106500,
    estimatedTaxLiability: 23430,
    estimatedRefund: 0,
  })

  const [showAddDocument, setShowAddDocument] = useState(false)
  const [generatingReport, setGeneratingReport] = useState(false)

  const categories = ['Income', 'Deductions', 'Credits', 'Business Expenses', 'Investment Income']

  const generateTaxReport = () => {
    setGeneratingReport(true)
    setTimeout(() => {
      setGeneratingReport(false)
      alert('Tax report generated successfully!')
    }, 2000)
  }

  const downloadReport = () => {
    const reportContent = `
TAX PREPARATION REPORT
======================

Prepared for Tax Agent Review
Generated: ${new Date().toLocaleString()}

INCOME SUMMARY
--------------
Total Income: $${reportData.totalIncome.toLocaleString()}
  - W-2 Income: $${(reportData.totalIncome * 0.8).toLocaleString()}
  - 1099 Income: $${(reportData.totalIncome * 0.2).toLocaleString()}

DEDUCTIONS
----------
Standard/Itemized Deductions: $${reportData.deductions.toLocaleString()}
  - Charitable Donations: $${(reportData.deductions * 0.3).toLocaleString()}
  - Medical Expenses: $${(reportData.deductions * 0.2).toLocaleString()}
  - Business Expenses: $${(reportData.deductions * 0.5).toLocaleString()}

TAXABLE INCOME
--------------
Total Deductions: $${reportData.deductions.toLocaleString()}
Taxable Income: $${reportData.taxableIncome.toLocaleString()}

TAX CALCULATION
---------------
Federal Tax Liability: $${reportData.estimatedTaxLiability.toLocaleString()}
Tax Rate: ${((reportData.estimatedTaxLiability / reportData.totalIncome) * 100).toFixed(2)}%

DOCUMENTS ATTACHED
------------------
${documents.map((d) => `- ${d.name} (${d.category}) - ${d.uploadDate}`).join('\n')}

NOTES FOR TAX AGENT
-------------------
All income sources have been documented. Please review and advise on any additional deductions or credits that may apply to your situation.
`

    const element = document.createElement('a')
    element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(reportContent))
    element.setAttribute('download', `Tax-Preparation-Report-${new Date().getFullYear()}.txt`)
    element.style.display = 'none'
    document.body.appendChild(element)
    element.click()
    document.body.removeChild(element)
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">Tax Preparation</h1>
        <p className="text-blue-200">Organize your documents and prepare tax report for your agent</p>
      </div>

      {/* Tax Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-blue-600/20 to-cyan-600/20 rounded-lg border border-blue-500/30 p-6">
          <p className="text-blue-300/70 text-sm mb-1">Total Income</p>
          <p className="text-3xl font-bold text-white">${reportData.totalIncome.toLocaleString()}</p>
          <p className="text-xs text-blue-300 mt-2">From all sources</p>
        </div>

        <div className="bg-gradient-to-br from-green-600/20 to-emerald-600/20 rounded-lg border border-green-500/30 p-6">
          <p className="text-green-300/70 text-sm mb-1">Total Deductions</p>
          <p className="text-3xl font-bold text-green-400">${reportData.deductions.toLocaleString()}</p>
          <p className="text-xs text-green-300 mt-2">Tax deductible items</p>
        </div>

        <div className="bg-gradient-to-br from-purple-600/20 to-pink-600/20 rounded-lg border border-purple-500/30 p-6">
          <p className="text-purple-300/70 text-sm mb-1">Est. Tax Liability</p>
          <p className="text-3xl font-bold text-purple-400">${reportData.estimatedTaxLiability.toLocaleString()}</p>
          <p className="text-xs text-purple-300 mt-2">Federal tax estimate</p>
        </div>
      </div>

      {/* Documents Section */}
      <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-white flex items-center space-x-2">
            <FileText size={24} />
            <span>Tax Documents</span>
          </h2>
          <button
            onClick={() => setShowAddDocument(!showAddDocument)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-semibold"
          >
            + Add Document
          </button>
        </div>

        {showAddDocument && (
          <div className="bg-slate-700/30 rounded-lg p-4 border border-blue-500/20 mb-4">
            <div className="space-y-3">
              <div>
                <label className="text-blue-300 font-semibold text-sm mb-2 block">Document Name</label>
                <input
                  type="text"
                  placeholder="e.g., W-2 Form 2024"
                  className="w-full px-4 py-2 bg-slate-800/50 border border-blue-500/30 rounded-lg text-white placeholder-blue-300/50"
                />
              </div>

              <div>
                <label className="text-blue-300 font-semibold text-sm mb-2 block">Category</label>
                <select className="w-full px-4 py-2 bg-slate-800/50 border border-blue-500/30 rounded-lg text-white">
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-blue-300 font-semibold text-sm mb-2 block">Upload File</label>
                <div className="border-2 border-dashed border-blue-500/30 rounded-lg p-8 text-center cursor-pointer hover:border-blue-500/60 transition-colors">
                  <Upload className="mx-auto text-blue-400 mb-2" size={32} />
                  <p className="text-blue-200 font-semibold">Click or drag to upload</p>
                  <p className="text-blue-300/70 text-sm">PDF, JPG, or PNG accepted</p>
                </div>
              </div>

              <button className="w-full px-4 py-2 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-lg font-bold hover:shadow-lg hover:shadow-blue-500/50 transition-all">
                Upload Document
              </button>
            </div>
          </div>
        )}

        <div className="space-y-3">
          {documents.map((doc) => (
            <div key={doc.id} className="bg-slate-700/30 rounded-lg p-4 border border-blue-500/20 flex items-center justify-between">
              <div className="flex items-center space-x-4 flex-1">
                <FileText size={24} className="text-blue-400" />
                <div className="flex-1">
                  <p className="font-semibold text-white">{doc.name}</p>
                  <div className="flex items-center space-x-3 text-xs text-blue-300/70 mt-1">
                    <span>{doc.category}</span>
                    <span>•</span>
                    <span>{doc.uploadDate}</span>
                  </div>
                </div>
              </div>
              <span className={`text-xs font-bold px-3 py-1 rounded-full ${
                doc.status === 'Uploaded' ? 'bg-green-500/30 text-green-300' : 'bg-yellow-500/30 text-yellow-300'
              }`}>
                {doc.status}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Tax Breakdown */}
      <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-6">
        <h2 className="text-xl font-bold text-white mb-4 flex items-center space-x-2">
          <Calculator size={24} />
          <span>Tax Calculation Summary</span>
        </h2>

        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg border border-blue-500/20">
            <span className="text-blue-200">Total Income</span>
            <span className="text-white font-bold">${reportData.totalIncome.toLocaleString()}</span>
          </div>

          <div className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg border border-blue-500/20">
            <span className="text-blue-200">Less: Deductions</span>
            <span className="text-white font-bold">-${reportData.deductions.toLocaleString()}</span>
          </div>

          <div className="flex items-center justify-between p-3 bg-blue-500/10 rounded-lg border border-blue-500/30">
            <span className="text-blue-300 font-semibold">Taxable Income</span>
            <span className="text-blue-300 font-bold">${reportData.taxableIncome.toLocaleString()}</span>
          </div>

          <div className="flex items-center justify-between p-3 bg-red-500/10 rounded-lg border border-red-500/30">
            <span className="text-red-300 font-semibold">Est. Tax Liability</span>
            <span className="text-red-300 font-bold">${reportData.estimatedTaxLiability.toLocaleString()}</span>
          </div>

          <div className="flex items-center justify-between p-3 bg-green-500/10 rounded-lg border border-green-500/30">
            <span className="text-green-300 font-semibold">Effective Tax Rate</span>
            <span className="text-green-300 font-bold">{((reportData.estimatedTaxLiability / reportData.totalIncome) * 100).toFixed(2)}%</span>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="grid grid-cols-2 gap-4">
        <button
          onClick={generateTaxReport}
          disabled={generatingReport}
          className="px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-lg font-bold hover:shadow-lg hover:shadow-blue-500/50 transition-all disabled:opacity-50 flex items-center justify-center space-x-2"
        >
          <Zap size={20} />
          <span>{generatingReport ? 'Generating...' : 'Generate Tax Report'}</span>
        </button>

        <button
          onClick={downloadReport}
          className="px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg font-bold hover:shadow-lg hover:shadow-green-500/50 transition-all flex items-center justify-center space-x-2"
        >
          <Download size={20} />
          <span>Download Report</span>
        </button>
      </div>

      {/* Note to Tax Agent */}
      <div className="bg-yellow-500/10 rounded-lg border border-yellow-500/30 p-6">
        <h3 className="text-lg font-bold text-yellow-300 mb-2 flex items-center space-x-2">
          <AlertCircle size={20} />
          <span>For Your Tax Agent</span>
        </h3>
        <p className="text-yellow-200 text-sm">
          All documents and financial information have been organized and summarized in this report. Please review and provide to your tax agent for professional tax filing. The estimates provided are for planning purposes and should be verified by your tax professional.
        </p>
      </div>
    </div>
  )
}
